import {Component, Inject, Input, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {TranslocoModule, TranslocoService} from '@jsverse/transloco';
import {NgSelectModule} from '@ng-select/ng-select';
import {
    IPlaybookDefaultFilter
} from "../../legal-hub-playbooks-listening/legal-hub-basic-filters/legal-hub-basic-filters.component";
import {PlaybookListeningApiService} from '../../playbook-listening.api.service';
import {
    BehaviorSubject,
    combineLatest,
    debounceTime,
    delay,
    delayWhen,
    distinctUntilChanged,
    filter,
    finalize,
    forkJoin,
    map,
    Observable,
    of,
    startWith,
    Subject,
    switchMap,
    take,
    takeUntil,
    tap
} from 'rxjs';
import {IGetPlaybookGuidelinesDTO, IPlaybooksItemDTO, PlaybookStatusEnum} from '../../dto/playbooks-listening.dto';
import {CommonModule} from '@angular/common';
import {NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle} from '@ng-bootstrap/ng-bootstrap';
import {CoreDirectivesModule} from '@core/directives/directives';
import {NgScrollbarModule} from 'ngx-scrollbar';
import {ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {
    IPlaybookGuidelineRunItem,
    PlaybookGuidelineRunTileComponent,
    PlaybookGuidelineStatusEnum
} from "./plyabbok-guideline-run-tile/playbook-guideline-run-tile.component";
import {
    ICreateNewPayloadRun,
    IPlaybookRunResultDTO,
    IPlaybookRunsListDTO,
    IRunPlaybookApiService,
    RunPlaybookApiServiceToken
} from "../document-playbook-run-api.service";
import {GuidelineListFilterPipe} from "./guideline-list-filter.pipe";
import {
    GuidelineListStatusFilterComponent
} from "./guideline-list-status-filter/guideline-list-status-filter.component";
import {GlobalService} from "../../../../../global/global.service";
import { IPlaybookListFacadeServiceToken, IPlaybookListFacadeService } from './plyabbok-guideline-run-tile/playbook-list.facade.service';


export const mapIPlaybookRunResultDTOTOIPlaybooksByIdTO = (data:IPlaybookRunResultDTO):IPlaybookGuidelineRunItem  =>{
  return {
      status: data.status,
      id: data.playbook_guideline_id,
      content: data.playbook_guideline?.content ?? '',
      details: {
          guideLineContent: data.playbook_guideline?.content ?? '',
          reason: data.reason,
          reference: data.reference,
          suggestion: data.suggestion,
          status: data.status,
      }
  }
}

export const mapIGetPlaybookGuidelinesDTOTOIPlaybookGuidelineRunItem = (data:Partial<IGetPlaybookGuidelinesDTO>, translocoService: TranslocoService):IPlaybookGuidelineRunItem =>{
    return {
        status: PlaybookGuidelineStatusEnum.MISSING,
        id: data.id,
        content: data.content ?? '',
        details: {
            guideLineContent: data.content?? '',
            status: PlaybookGuidelineStatusEnum.MISSING,
            reason: translocoService.translate('Playbooks not run on document/template reason section explanation (Playbook run details view) - Requires our AI to analyze the document'),
            reference:translocoService.translate('Playbooks not run on document/template reference section explanation (Playbook run details view) - Requires our AI to find reference'),
        }
    }
}

export interface IGuideLineListFilter{
    status?: PlaybookGuidelineStatusEnum[];
}

@Component({
  selector: 'app-playbook-list',
  standalone: true,
    imports: [
        NgbDropdown,
        CoreDirectivesModule,
        NgScrollbarModule,
        TranslocoModule,
        NgSelectModule,
        CommonModule,
        ReactiveFormsModule,
        NgbDropdownItem,
        NgbDropdownMenu,
        NgbDropdownToggle,
        PlaybookGuidelineRunTileComponent,
        GuidelineListFilterPipe,
        GuidelineListStatusFilterComponent
    ],
  templateUrl: './playbook-list.component.html',
  styleUrl: './playbook-list.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class PlaybookListComponent implements OnInit, OnDestroy {
  private searchSubject: Subject<string> = new Subject<string>();
  private unsubscribe$: Subject<void> = new Subject<void>();
    /**
     *  The id of the object that the playbook is linked to. ex Document or Template
     */
  @Input({required: true}) referenceObjectId:number;

  public playbookForm: UntypedFormGroup;
  playbooks$:Subject<IPlaybooksItemDTO[]> = new Subject<IPlaybooksItemDTO[]>();
  playbooksRuns$:Subject<IPlaybookRunsListDTO[]> = new BehaviorSubject<IPlaybookRunsListDTO[]>([]);//Playbook runs for specifict document
  playbooksRunsLoading$:Subject<boolean> = new BehaviorSubject(false);
  playbookRunning$:Subject<boolean> = new BehaviorSubject(false);
  existingPlaybookRun$:Subject<IPlaybookRunsListDTO> = new BehaviorSubject<IPlaybookRunsListDTO>(null);
  playbooksLoading$:Subject<boolean> = new BehaviorSubject(false);
  guideLines$: Subject<IPlaybookGuidelineRunItem[]> = new BehaviorSubject<IPlaybookGuidelineRunItem[]>([]);
  guideLinesLoading$: Subject<boolean> = new BehaviorSubject(false);
  guideFilters$: Observable<IGuideLineListFilter> = this._playbookFacadeService.legalHubFilters$;
  selectedPlaybook$: Observable<IPlaybooksItemDTO> = this._playbookFacadeService.legalHubSelectedPlaybook$;
  areAllGuidelineRunDataReady$:Observable<boolean> = combineLatest([this.playbooksRunsLoading$, this.playbooksLoading$])
      .pipe(
          startWith([true, true]),
          delay(300),
          map(x=>x.every(y=>y === false))
      );
   showPlaybookRunButton$:Observable<boolean> = combineLatest([
       this.existingPlaybookRun$.pipe(map(x=> x === null)),
       this.playbookRunning$.pipe(map(x=> x === false)),
       this.guideLinesLoading$.pipe(map(x=> x === false)),
      ])
      .pipe(
          map(x=> x.every(y=> y === true))
      );   

  constructor(
    private _playbookListingAPIService: PlaybookListeningApiService,
    private _formBuilder: UntypedFormBuilder,
    private globalService: GlobalService,
    private _translocoService: TranslocoService,
    @Inject(IPlaybookListFacadeServiceToken) private _playbookFacadeService: IPlaybookListFacadeService,
    @Inject(RunPlaybookApiServiceToken) private _runPlaybookApiService: IRunPlaybookApiService
  ) { 
  }


  ngOnInit(): void {

   this.updatePlaybookRuns();

   this.searchSubject.pipe(
     debounceTime(300),
     switchMap((queryString: string) => {
       return  this._playbookListingAPIService.getPlaybooks(null, queryString, {status:PlaybookStatusEnum.published} as IPlaybookDefaultFilter)
           .pipe(
               tap(()=> this.playbooksLoading$.next(true)),
               finalize(()=> this.playbooksLoading$.next(false)),
           );
     }),
     takeUntil(this.unsubscribe$)
   ).subscribe({
        next:(response) => {
         this.playbooks$.next(response.data);
        }
   });

   this.searchSubject.next(null);

    this.playbookForm = this._formBuilder.group({
        playbook: [null],
    });

      this.playbookForm?.get('playbook')?.valueChanges
          .pipe(
              tap(()=> this.guideLinesLoading$.next(true)),
              debounceTime(500),
              takeUntil(this.unsubscribe$)
          )
          .subscribe((playbook)=> this._playbookFacadeService.setLegalHubPlaybook(playbook))

    this.selectedPlaybook$
    .pipe(
      distinctUntilChanged(),
      delayWhen((x)=>this.areAllGuidelineRunDataReady$.pipe(filter(x=>x === true), map(()=> x) )),

      tap((playbbook)=> {
          this.playbookForm?.get('playbook')?.setValue(playbbook);
          this.playbookRunning$.next(false);
          this.existingPlaybookRun$.next(null);
          this.setDefaultFilter()
      }),
      switchMap((playbook) => playbook
      ? this.playbooksRuns$
              .pipe(
                  take(1),
                  map(x=>this.findLatestRun(x, playbook.id)),
                  switchMap(existingPlaybookRun=>{
                  this.existingPlaybookRun$.next(existingPlaybookRun);
                  return existingPlaybookRun
                       ? forkJoin({
                          playbook: this._playbookListingAPIService.getPlaybookById(playbook.id).pipe(map(x=> x.playbook_guidelines.map((data)=>mapIGetPlaybookGuidelinesDTOTOIPlaybookGuidelineRunItem(data, this._translocoService)))),
                          playbookRun: this._runPlaybookApiService.getSpecificPlaybookRunById(existingPlaybookRun.id)
                              .pipe(
                                  map(x=>x.playbook_run_results.map(mapIPlaybookRunResultDTOTOIPlaybooksByIdTO))
                              )
                      }).pipe(
                          map( ( {playbook, playbookRun})=>{
                              return playbook.map(y=> {
                                  const playbookRunItem = playbookRun.find(z=>z.id === y.id);
                                  return playbookRunItem ? playbookRunItem
                                      : {
                                            ...y,
                                          status: PlaybookGuidelineStatusEnum.MISSING,
                                        }
                              })

                          })
                      )

                      : this._playbookListingAPIService.getPlaybookById(playbook.id).pipe(map(x=> x.playbook_guidelines.map((data)=>mapIGetPlaybookGuidelinesDTOTOIPlaybookGuidelineRunItem(data, this._translocoService))))

              }))
      : of(null)),
      takeUntil(this.unsubscribe$)
    )
    .subscribe(
      {
        next:(guidelines)=>{
          this.guideLines$.next(guidelines);
          this.guideLinesLoading$.next(false);
          this.updatePlaybookRuns();
        },
        error:()=>{
            this.guideLinesLoading$.next(false);
        }
      })
  }

  searchPlaybook(search: string) {
      this.searchSubject.next(search);
  }

  openLegalHubSidebar(guideLine:IPlaybookGuidelineRunItem): void {
      this._playbookFacadeService.openLegalHubSidebar(guideLine);
  }

  runPlaybook():void{
     const payload: ICreateNewPayloadRun = {
         id: this.referenceObjectId,
         playbook_id: Number(this.playbookForm.get('playbook')?.value.id)
      }

     this.guideLines$.pipe(
         take(1),
         tap(guidelines=>{
             this.playbookRunning$.next(true);
             if(Array.isArray(guidelines) && guidelines.length > 0){
                 this.guideLines$.next(guidelines.map(y=>({
                     ...y,
                     status: PlaybookGuidelineStatusEnum.PROCESSING,
                 })))
             }
         }),

         switchMap( ()=> this._runPlaybookApiService.runPlaybook(payload)),
         finalize(()=> this.playbookRunning$.next(false)),
         take(1))
         .subscribe({
             next: (runResult:IPlaybookRunsListDTO) => {
                 this.guideLines$.next(runResult.playbook_run_results.map(mapIPlaybookRunResultDTOTOIPlaybooksByIdTO));
                 this.existingPlaybookRun$.next(runResult);
             },
             error:(error) =>{
                 this.globalService.toastrError(error);
             }
         });
  }

  onGuideLineItemClick(guideLine:IPlaybookGuidelineRunItem):void{
       this.openLegalHubSidebar(guideLine)
  }
 
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  filterByStatus(status: PlaybookGuidelineStatusEnum[]): void {
    this._playbookFacadeService.setLegalHubFilters({status: status})
  }


    runPlaybookAgain():void{
      this.clearReview()
          .pipe(
              take(1)
          )
          .subscribe({
              next:()=>{
                  this.runPlaybook();
              }
          })
    }

    applyAllSuggestions():void{
      /*
      //TODO: implement later as it's v4 scope
       */
    }

    onClearReviewClicked(){
      this.clearReview()
          .pipe(
              take(1),
              takeUntil(this.unsubscribe$)
          ).subscribe();
    }
    private clearReview = (): Observable<void> => new Observable<void>((observer) => {
        this.existingPlaybookRun$
            .pipe(
                switchMap(x => x ? this._runPlaybookApiService.deletePlaybookRun(x.id)
                                    .pipe(tap(()=>this.updatePlaybookRuns()))
                                  : of(null)
                ),
                switchMap(() => this.guideLines$),
                take(1),
            )
            .subscribe({
                next: (guidekines) => {
                    this.existingPlaybookRun$.next(null);
                    this.setDefaultFilter()
                    this.guideLines$.next(guidekines.map((data) => mapIGetPlaybookGuidelinesDTOTOIPlaybookGuidelineRunItem(data, this._translocoService)));
                    observer.next();
                },
                error: (error) => {
                    observer.error(error);
                }
            })
    });

    private findLatestRun(runs :IPlaybookRunsListDTO[], playbookId:number):IPlaybookRunsListDTO{
        return (runs ?? [])
            .sort((a,b)=> a.created_at < b.created_at ? 1 : -1)
            .find(y=>y.playbook_id === playbookId) ?? null;
    }

    private setDefaultFilter(){
      this._playbookFacadeService.setLegalHubFilters({status: [PlaybookGuidelineStatusEnum.MISSING, PlaybookGuidelineStatusEnum.NOT_MET, PlaybookGuidelineStatusEnum.MET]});
    }

    private updatePlaybookRuns(){
        this.playbooksRunsLoading$.next(true);
        this._runPlaybookApiService.getPlaybookRunForById(this.referenceObjectId)
            .pipe(
                take(1),
                takeUntil(this.unsubscribe$)
            )
            .subscribe(x=>{
                this.playbooksRunsLoading$.next(false);
                this.playbooksRuns$.next(x)
            });
    }
}
